import { registerSW } from "virtual:pwa-register";

let didRegisterPwa = false;
let refreshing = false;

const isKioskRoute = (): boolean => window.location.pathname.startsWith("/kiosk");

const unregisterRootScopedServiceWorkers = async (): Promise<boolean> => {
  const rootScope = `${window.location.origin}/`;
  const registrations = await navigator.serviceWorker.getRegistrations();
  const rootScopedRegistrations = registrations.filter(
    (registration) => registration.scope === rootScope,
  );

  if (rootScopedRegistrations.length === 0) {
    return false;
  }

  await Promise.all(rootScopedRegistrations.map((registration) => registration.unregister()));
  return true;
};

const registerKioskServiceWorker = (): void => {
  let hasController = navigator.serviceWorker.controller !== null;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hasController) {
      hasController = true;
      return;
    }

    if (refreshing) {
      return;
    }

    refreshing = true;
    window.location.reload();
  });

  const updateServiceWorker = registerSW({
    immediate: true,

    onNeedRefresh() {
      void updateServiceWorker(true);
    },

    onRegisteredSW(_swUrl, registration) {
      if (!registration) {
        return;
      }

      const updateIntervalId = setInterval(
        () => {
          // The registration's worker can become redundant/unregistered while
          // this long-lived interval keeps firing. Calling update() in that
          // state rejects in Safari with "InvalidStateError: newestWorker is
          // null", so stop the timer and skip when there is no worker left.
          const hasWorker =
            registration.installing !== null ||
            registration.waiting !== null ||
            registration.active !== null;

          if (!hasWorker) {
            clearInterval(updateIntervalId);
            return;
          }

          registration.update().catch((error: unknown) => {
            // Transient network/offline failures while fetching sw.js are expected
            // on kiosk devices. Swallow them so they don't surface as unhandled
            // promise rejections; the next interval will retry the update.
            console.debug("[PWA] Service worker update check failed", error);
          });
        },
        5 * 60 * 1000,
      );
    },

    onRegisterError(error: unknown) {
      console.error("[PWA] Service worker registration failed", error);
    },
  });
};

export const registerPwa = (): void => {
  if (
    didRegisterPwa ||
    typeof window === "undefined" ||
    import.meta.env.DEV ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }

  didRegisterPwa = true;

  void unregisterRootScopedServiceWorkers()
    .then((removedRootScopedRegistration) => {
      if (removedRootScopedRegistration && navigator.serviceWorker.controller) {
        window.location.reload();
        return;
      }

      if (isKioskRoute()) {
        registerKioskServiceWorker();
      }
    })
    .catch((error: unknown) => {
      console.error("[PWA] Service worker migration failed", error);

      if (isKioskRoute()) {
        registerKioskServiceWorker();
      }
    });
};
